import { TypographyTheme, TypographyVariant } from "./Types";

const generateTypographyVariant = (
  fontFamily: TypographyVariant["font-family"],
  fontWeight: TypographyVariant["font-weight"],
  fontSize: TypographyVariant["font-size"],
  lineHeight: TypographyVariant["line-height"],
  letterSpacing: TypographyVariant["letter-spacing"]
) => {
  return {
    "font-family": fontFamily,
    "font-weight": fontWeight,
    "font-size": fontSize,
    "line-height": lineHeight,
    "letter-spacing": letterSpacing,
  };
};

const serif = "var(--slate-xp_typography-family-serif, nantes, georgia, serif)";
const sans =
  "var(--slate-xp_typography-family-sans-serif, graphik, helvetica, sans-serif)";
const labelSans = `var(--slate-xp_typography-family-label, ${sans})`;
// MJML validates letter-spacing as a Unit (px/em only) and rejects CSS `var(...)`.
const letterSpacing200 = "0.15px";

// TEMP (BSR experiment FE_SLATE_$0_KEY_BRAND_REFINEMENT_XP_261006): cleanup after the experiment finishes
const letterSpacing200Web = `var(--slate-xp_typography-letter-spacing-200, ${letterSpacing200})`;
export const withWebLetterSpacing = (
  variant: TypographyVariant
): TypographyVariant =>
  variant && variant["letter-spacing"] === letterSpacing200
    ? { ...variant, "letter-spacing": letterSpacing200Web }
    : variant;

const fonts: TypographyTheme["fonts"] = {
  displayXLSerifRegular: generateTypographyVariant(
    serif,
    400,
    "72px",
    "86px",
    0
  ),
  displayLSerifRegular: generateTypographyVariant(
    serif,
    400,
    "52px",
    "64px",
    0
  ),
  displayMSerifRegular: generateTypographyVariant(
    serif,
    400,
    "38px",
    "50px",
    0
  ),
  displaySSerifRegular: generateTypographyVariant(
    serif,
    400,
    "30px",
    "38px",
    0
  ),
  displaySSansRegular: generateTypographyVariant(
    sans,
    "var(--slate-xp_typography-weight-display-s, 200)",
    "30px",
    "38px",
    letterSpacing200
  ),
  displaySSansSemiBold: generateTypographyVariant(
    sans,
    "var(--slate-xp_typography-weight-display-s-semibold, 700)",
    "30px",
    "38px",
    letterSpacing200
  ),
  pageHeaderSerifRegular: generateTypographyVariant(
    serif,
    400,
    "22px",
    "32px",
    0
  ),
  sectionHeaderSansMedium: generateTypographyVariant(
    sans,
    "var(--slate-xp_typography-weight-section-heading, 400)",
    "22px",
    "32px",
    letterSpacing200
  ),
  subheadingSansMedium: generateTypographyVariant(
    sans,
    "var(--slate-xp_typography-weight-subheading-medium, 500)",
    "18px",
    "26px",
    letterSpacing200
  ),
  subheadingSansRegular: generateTypographyVariant(
    sans,
    "var(--slate-xp_typography-weight-subheading, 200)",
    "18px",
    "26px",
    letterSpacing200
  ),
  paragraphSansMedium: generateTypographyVariant(
    sans,
    "var(--slate-xp_typography-weight-paragraph-medium, 500)",
    "14px",
    "20px",
    letterSpacing200
  ),
  paragraphSansRegular: generateTypographyVariant(
    sans,
    "var(--slate-xp_typography-weight-paragraph, 200)",
    "14px",
    "20px",
    letterSpacing200
  ),
  labelSansRegular: generateTypographyVariant(
    labelSans,
    "var(--slate-xp_typography-weight-label, 200)",
    "12px",
    "16px",
    letterSpacing200
  ),
  labelSansMedium: generateTypographyVariant(
    labelSans,
    "var(--slate-xp_typography-weight-label-medium, 500)",
    "12px",
    "16px",
    letterSpacing200
  ),
};

export const fontRamp: TypographyTheme = {
  fonts,
};
